import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from './config';
import { DateTime } from 'luxon';
import * as Select from '@radix-ui/react-select';
import BrandHeader from './BrandHeader';

const BookingLog = ({ jwt, isPowerUser = false }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'success', 'error', 'app', 'user'
  const [selectedScheduleId, setSelectedScheduleId] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('me'); // 'me', 'all', or specific user email
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextKey, setNextKey] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState(new Set());

  const fetchSchedules = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_BASE}/schedule/mine`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setSchedules(resp.data || []);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    }
  }, [jwt]);

  const fetchUsers = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setUsers(resp.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [jwt]);

  const fetchLogs = useCallback(async ({ loadMore = false, paginationKey = null } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: 50,
      };
      // Only include schedule_id if not viewing all users (schedule_id is user-specific)
      if (selectedScheduleId && selectedScheduleId !== 'all' && !(isPowerUser && selectedUserId === 'all')) {
        params.schedule_id = selectedScheduleId;
      }
      if (isPowerUser && selectedUserId && selectedUserId !== 'me') {
        params.filter_user_id = selectedUserId;
      }
      if (loadMore && paginationKey) {
        params.last_evaluated_key = paginationKey;
      }
      
      const resp = await axios.get(`${API_BASE}/schedule/logs`, {
        headers: { Authorization: `Bearer ${jwt}` },
        params,
      });
      
      if (loadMore) {
        setLogs(prevLogs => [...prevLogs, ...(resp.data.data || [])]);
      } else {
        setLogs(resp.data.data || []);
        // Reset pagination when not loading more
        setNextKey(resp.data.next_key || null);
        setHasMore(resp.data.has_more || false);
      }
      
      // Update pagination state
      if (loadMore) {
        setHasMore(resp.data.has_more || false);
        setNextKey(resp.data.next_key || null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch booking logs');
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [isPowerUser, jwt, selectedScheduleId, selectedUserId]);

  useEffect(() => {
    fetchSchedules();
    if (isPowerUser) {
      fetchUsers();
    }
  }, [fetchSchedules, fetchUsers, isPowerUser]);

  useEffect(() => {
    // Reset pagination when filters change
    setNextKey(null);
    setHasMore(false);
    fetchLogs();
  }, [fetchLogs]);

  const toggleExpand = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const dt = DateTime.fromISO(timestamp);
    const now = DateTime.now();
    const diff = now.diff(dt, ['days', 'hours', 'minutes']);
    
    if (diff.days >= 1) {
      return `${Math.floor(diff.days)} day${Math.floor(diff.days) !== 1 ? 's' : ''} ago`;
    } else if (diff.hours >= 1) {
      return `${Math.floor(diff.hours)} hour${Math.floor(diff.hours) !== 1 ? 's' : ''} ago`;
    } else if (diff.minutes >= 1) {
      return `${Math.floor(diff.minutes)} minute${Math.floor(diff.minutes) !== 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const formatClassDateTime = (classDatetime) => {
    if (!classDatetime) return null;
    try {
      const dt = DateTime.fromFormat(
        classDatetime.date,
        'yyyy-MM-dd HH:mm:ss',
        { zone: classDatetime.timezone || 'Asia/Jerusalem' }
      );
      return dt.toFormat('EEE, MMM d, yyyy • h:mm a');
    } catch (e) {
      return classDatetime.date || null;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'user_booking':
        return 'You booked';
      case 'app_booking':
        return 'Auto-booked';
      case 'user_cancel':
        return 'You cancelled';
      case 'app_cancel':
        return 'Auto-cancelled';
      default:
        return actionType;
    }
  };

  const getStatusIcon = (status, actionType) => {
    if (status === 'success') {
      return '✓';
    } else if (status === 'error') {
      return '✗';
    } else if (status === 'skipped') {
      return '⊘';
    } else if (status === 'partial_success') {
      return '⚠';
    }
    return '•';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'skipped':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'partial_success':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const filteredLogs = logs.filter((log) => {
    if (filter === 'all') return true;
    if (filter === 'success') return log.status === 'success';
    if (filter === 'error') return log.status === 'error';
    if (filter === 'app') return log.action_type === 'app_booking' || log.action_type === 'app_cancel';
    if (filter === 'user') return log.action_type === 'user_booking' || log.action_type === 'user_cancel';
    return true;
  });

  const formatScheduleName = (schedule) => {
    if (!schedule) return '';
    const className = schedule.class_name || 'Unknown Class';
    
    // Parse date_time to get day of week and time
    try {
      const dateTime = schedule.date_time;
      if (dateTime && dateTime.date) {
        const dt = DateTime.fromFormat(
          dateTime.date,
          'yyyy-MM-dd HH:mm:ss',
          { zone: dateTime.timezone || 'Asia/Jerusalem' }
        );
        const dayOfWeek = dt.toFormat('EEE'); // e.g., "Thu"
        const time = dt.toFormat('h:mm a'); // e.g., "7:00 AM"
        return `${className} - ${dayOfWeek} ${time}`;
      }
    } catch (e) {
      console.error('Error formatting schedule name:', e);
    }
    
    return className;
  };

  return (
    <div className="min-h-screen bg-[#f8fbff] py-4 md:py-8 box-border">
      <div className="max-w-4xl mx-auto px-4">
        <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(31,41,55,0.10)] w-full box-border">
          <BrandHeader title="Booking Activity Log" />
          
          <div className="pb-6 md:p-8 w-full box-border px-4 md:px-0">
            {/* Filters */}
            <div className="mb-6 space-y-4 pt-4">
              {/* Status Filter Buttons - Modern Chip Style */}
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    filter === 'all'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('success')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    filter === 'success'
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Success
                </button>
                <button
                  onClick={() => setFilter('error')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    filter === 'error'
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Errors
                </button>
                <button
                  onClick={() => setFilter('app')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    filter === 'app'
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  App Actions
                </button>
                <button
                  onClick={() => setFilter('user')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    filter === 'user'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Your Actions
                </button>
              </div>
              
              {/* User Filter - Only for power users */}
              {isPowerUser && (
                <div className="flex flex-col items-center w-full">
                  <div className="w-full max-w-xs">
                    <Select.Root 
                      value={selectedUserId} 
                      onValueChange={(val) => {
                        setSelectedUserId(val);
                      }}
                    >
                      <Select.Trigger className="w-full py-2.5 px-4 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-black text-sm shadow-sm hover:border-gray-400 transition-all duration-200">
                        <Select.Value placeholder="Me">
                          {selectedUserId === 'me' 
                            ? 'Me' 
                            : selectedUserId === 'all'
                            ? 'All Users'
                            : selectedUserId}
                        </Select.Value>
                        <Select.Icon className="ml-2 text-gray-500">▼</Select.Icon>
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="w-full max-w-xs min-w-[10rem] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                          <Select.Viewport>
                            <Select.Item value="me" className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-black text-sm">
                              <Select.ItemText>Me</Select.ItemText>
                              {selectedUserId === 'me' && (
                                <Select.ItemIndicator className="ml-2 text-blue-600">✓</Select.ItemIndicator>
                              )}
                            </Select.Item>
                            <Select.Item value="all" className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-black text-sm">
                              <Select.ItemText>All Users</Select.ItemText>
                              {selectedUserId === 'all' && (
                                <Select.ItemIndicator className="ml-2 text-blue-600">✓</Select.ItemIndicator>
                              )}
                            </Select.Item>
                            {users.map((user) => (
                              <Select.Item 
                                key={user.email} 
                                value={user.email} 
                                className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-black text-sm"
                              >
                                <Select.ItemText>{user.email}</Select.ItemText>
                                {selectedUserId === user.email && (
                                  <Select.ItemIndicator className="ml-2 text-blue-600">✓</Select.ItemIndicator>
                                )}
                              </Select.Item>
                            ))}
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </div>
                </div>
              )}
              
              {/* Schedule Filter - Using Radix UI Select like location dropdown */}
              <div className="flex flex-col items-center w-full">
                <div className="w-full max-w-xs">
                  <Select.Root 
                    value={selectedScheduleId} 
                    onValueChange={(val) => {
                      setSelectedScheduleId(val);
                    }}
                    disabled={isPowerUser && selectedUserId === 'all'}
                  >
                    <Select.Trigger 
                      className={`w-full py-2.5 px-4 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-black text-sm shadow-sm hover:border-gray-400 transition-all duration-200 ${
                        isPowerUser && selectedUserId === 'all' ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={isPowerUser && selectedUserId === 'all'}
                    >
                      <Select.Value placeholder="All Schedules">
                        {isPowerUser && selectedUserId === 'all'
                          ? 'All Schedules (N/A for all users)'
                          : selectedScheduleId === 'all' 
                          ? 'All Schedules' 
                          : formatScheduleName(schedules.find(s => s.schedule_id === selectedScheduleId))}
                      </Select.Value>
                      <Select.Icon className="ml-2 text-gray-500">▼</Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="w-full max-w-xs min-w-[10rem] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                        <Select.Viewport>
                          <Select.Item value="all" className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-black text-sm">
                            <Select.ItemText>All Schedules</Select.ItemText>
                            {selectedScheduleId === 'all' && (
                              <Select.ItemIndicator className="ml-2 text-blue-600">✓</Select.ItemIndicator>
                            )}
                          </Select.Item>
                          {schedules.map((schedule) => (
                            <Select.Item 
                              key={schedule.schedule_id} 
                              value={schedule.schedule_id} 
                              className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-black text-sm"
                            >
                              <Select.ItemText>{formatScheduleName(schedule)}</Select.ItemText>
                              {selectedScheduleId === schedule.schedule_id && (
                                <Select.ItemIndicator className="ml-2 text-blue-600">✓</Select.ItemIndicator>
                              )}
                            </Select.Item>
                          ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>
              </div>
            </div>

            {loading && logs.length === 0 && (
              <div className="text-gray-500 text-center py-8">Loading logs...</div>
            )}
            
            {error && (
              <div className="bg-red-100 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}
            
            {!loading && !error && filteredLogs.length === 0 && (
              <div className="text-gray-500 text-center py-8">
                No booking activity found.
              </div>
            )}
            
            {!loading && !error && filteredLogs.length > 0 && (
              <div className="space-y-4">
                {filteredLogs.map((log) => {
                  const isExpanded = expandedLogs.has(log.log_id);
                  const classDateTime = formatClassDateTime(log.class_datetime);
                  
                  return (
                    <div
                      key={log.log_id}
                      className={`border rounded-lg p-4 bg-white shadow-sm transition-all ${
                        log.status === 'error' ? 'border-red-200' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`text-2xl font-bold ${getStatusColor(log.status).split(' ')[0]}`}>
                              {getStatusIcon(log.status, log.action_type)}
                            </span>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-gray-800">
                                  {getActionLabel(log.action_type)}
                                </span>
                                {isPowerUser && log.user_id && (
                                  <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 border border-gray-300 font-medium">
                                    {log.user_id}
                                  </span>
                                )}
                                <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(log.status)}`}>
                                  {log.status}
                                </span>
                                {log.booking_type && (
                                  <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 border border-blue-200">
                                    {log.booking_type}
                                  </span>
                                )}
                              </div>
                              {log.class_name && (
                                <div className="text-lg font-medium text-gray-900 mt-1">
                                  {log.class_name}
                                </div>
                              )}
                              {classDateTime && (
                                <div className="text-sm text-gray-600 mt-1">
                                  {classDateTime}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {log.error_message && (
                            <div className="mt-2 text-sm text-red-600">
                              {log.error_message}
                            </div>
                          )}
                          
                          {log.status === 'success' && !log.error_message && (
                            <div className="mt-2 text-sm text-green-600">
                              Successfully {log.action_type.includes('booking') ? 'booked' : 'cancelled'}
                            </div>
                          )}
                          
                          <div className="mt-2 text-xs text-gray-500">
                            {formatTimestamp(log.timestamp)}
                          </div>
                        </div>
                        
                        {(log.error_details || log.metadata) && (
                          <button
                            onClick={() => toggleExpand(log.log_id)}
                            className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <span className="material-icons">
                              {isExpanded ? 'expand_less' : 'expand_more'}
                            </span>
                          </button>
                        )}
                      </div>
                      
                      {isExpanded && (log.error_details || log.metadata) && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          {log.error_details && (
                            <div className="mb-3">
                              <div className="text-xs font-semibold text-gray-700 mb-1">
                                Error Details:
                              </div>
                              <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40">
                                {JSON.stringify(log.error_details, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.metadata && (
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-1">
                                Metadata:
                              </div>
                              <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40">
                                {JSON.stringify(log.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {hasMore && (
                  <div className="text-center pt-4">
                    <button
                      onClick={() => fetchLogs({ loadMore: true, paginationKey: nextKey })}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingLog;
